import React, { useContext, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ShieldCheck, Medal, Users, Briefcase, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const ForCompaniesSection = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  return (
    <SectionWrapper id="for-companies">
      <ContentWrapper>
        {/* LEFT SIDE CONTENT */}
        <LeftContent
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>
            For Companies —{" "}
            <Highlight>Hire Verified & Proven Developers</Highlight>
          </Title>
          <Subtitle>
            Discover skilled developers who’ve proven their expertise by solving
            real coding problems. Hire confidently based on performance, not
            resumes.
          </Subtitle>

          <FeatureList>
            <li>
              <ShieldCheck size={18} /> Verified Developer Profiles
            </li>
            <li>
              <Medal size={18} /> Leaderboard-Based Rankings
            </li>
            <li>
              <Users size={18} /> Direct Chat & Quick Hiring
            </li>
          </FeatureList>

          <CTAButton
            onClick={() => {
              if (user) {
                navigate("/post-freelance");
              } else {
                setShowAuthModal(true);
              }
            }}
          >
            Post a Company Project
          </CTAButton>
        </LeftContent>

        {/* RIGHT SIDE VISUALS */}
        <RightVisual
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ProfileCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 150 }}
          >
            <ProfileHeader>
              <ProfileImage src="https://randomuser.me/api/portraits/men/45.jpg" />
              <div>
                <h4>Arjun Patel</h4>
                <p>Full-Stack Developer</p>
              </div>
              <Badge>Verified Solver</Badge>
            </ProfileHeader>
            <Stars>
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} color="#c59fff" fill="#c59fff" />
              ))}
            </Stars>
            <ProfileFooter>
              <Stat>
                <strong>45+</strong>
                <span>Issues Solved</span>
              </Stat>
              <Stat>
                <strong>₹22,000</strong>
                <span>Earned</span>
              </Stat>
              <Stat>
                <strong>#4</strong>
                <span>Global Rank</span>
              </Stat>
            </ProfileFooter>
          </ProfileCard>

          <ProjectCard
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 150 }}
          >
            <Briefcase size={30} color="#c59fff" />
            <ProjectTitle>React Developer Needed</ProjectTitle>
            <p>Company: CodeGenie Labs</p>
            <ApplyButton>View Applicants</ApplyButton>
          </ProjectCard>
        </RightVisual>
      </ContentWrapper>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
        }}
      />
    </SectionWrapper>
  );
};

export default ForCompaniesSection;

// ---------------- Styled Components ----------------

const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #0b0617, #000);
  color: #fff;
  padding: 120px 40px;
  display: flex;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  gap: 40px;
`;

const LeftContent = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 550px;
`;

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1rem;
    color: #e0e0e0;
    svg {
      color: #c59fff;
    }
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-weight: 600;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.3);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.6);
  }
`;

const RightVisual = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 500px;
  position: relative;
  height: 500px;
`;

const ProfileCard = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 300px;
  backdrop-filter: blur(6px);
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.2);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  h4 {
    font-size: 1.1rem;
    color: #fff;
    margin: 0;
  }
  p {
    font-size: 0.85rem;
    color: #aaa;
  }
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
`;

const Badge = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 5px;
`;

const ProfileFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 10px;

  strong {
    color: #c59fff;
    font-size: 1rem;
  }

  span {
    display: block;
    font-size: 0.8rem;
    color: #aaa;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const ProjectCard = styled(motion.div)`
  position: absolute;
  top: 280px;
  left: 40px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 260px;
  text-align: center;
  backdrop-filter: blur(6px);
`;

const ProjectTitle = styled.h3`
  color: #c59fff;
  font-size: 1.1rem;
  margin: 10px 0;
`;

const ApplyButton = styled.button`
  margin-top: 10px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;
